import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { applyLanguage, normalizeLanguage } from "@/i18n";

const AppContext = createContext(null);

export function AppProvider({ children }) {
    const [user, setUser] = useState(null); // null = loading, false = logged out
    const [restaurants, setRestaurants] = useState([]);
    const [restaurantId, setRestaurantId] = useState(null);

    const loadRestaurants = useCallback(async (currentUser) => {
        try {
            const { data } = await api.get("/restaurants");
            setRestaurants(data);
            // Default selection
            if (currentUser?.role === "restaurant") {
                setRestaurantId(currentUser.restaurant_id);
            } else if (data.length > 0) {
                const stored = localStorage.getItem("kop-restaurant");
                const match = data.find((r) => r.id === stored);
                setRestaurantId(match ? match.id : data[0].id);
            }
        } catch {
            setRestaurants([]);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/me");
                setUser(data);
                await loadRestaurants(data);
            } catch {
                setUser(false);
            }
        })();
    }, [loadRestaurants]);

    // Authenticated: the account preference wins (no preference → English),
    // regardless of the device cache left by a previous user.
    const accountLang = user ? normalizeLanguage(user.language_preference) : null;
    useEffect(() => {
        // Employee Mobile keeps its own device-local language.
        if (accountLang && !window.location.pathname.startsWith("/employee/")) applyLanguage(accountLang);
    }, [accountLang]);

    const setLanguagePreference = useCallback(async (lang) => {
        // The user object is not replaced: replacing it would re-run account-
        // scoped providers and remount the current screen (unsaved input lost).
        const next = applyLanguage(lang);
        await api.patch("/auth/me", { language_preference: next });
    }, []);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        setUser(data);
        await loadRestaurants(data);
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (_e) { /* noop */ }
        setUser(false);
        setRestaurants([]);
        setRestaurantId(null);
        localStorage.removeItem("kop-restaurant");
    };

    const selectRestaurant = (id) => {
        setRestaurantId(id);
        localStorage.setItem("kop-restaurant", id);
    };

    const currentRestaurant = restaurants.find((r) => r.id === restaurantId) || null;

    return (
        <AppContext.Provider
            value={{ user, restaurants, restaurantId, currentRestaurant,
                     selectRestaurant, login, logout, setLanguagePreference }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useApp() {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be inside AppProvider");
    return ctx;
}
